import React, { useContext, useEffect, useRef } from 'react';
import {
    formatFileSize,
    get,
    getPublicImageUrl,
    isEmpty,
    isImageExt,
    parseFileName
} from '~/utils';
import { IconFont } from '~/components';
import { Popover, Progress } from '@fx-ui/fine-design';
import { FileThumb } from '@fx-ui/jdy-design-biz';
import { IFile } from '~/interfaces';
import classNames from 'classnames';
import { EMPTY_FN, MAX_IMAGE_VIEW_SIZE } from '~/constants';
import { RegionContext } from '~/contexts';

enum UPLOAD_STYLE {
    SHORT = 'short' // 简短样式
}

interface IFileItemProps {
    className?: string;
    style?: React.CSSProperties;
    file: IFile | File;
    progress?: number;
    withLocalPreview?: boolean;
    fallbackPreview?: boolean;
    finish?: boolean;
    hasDeleteBtn?: boolean;
    hasDownloadBtn?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    onDelete?: () => void;
    onDownload?: () => void;
    maxWidth?: number;
    maxHeight?: number;
    uploadStyle?: UPLOAD_STYLE;
}

const FileItem: React.FC<IFileItemProps> = (props) => {
    const {
        className,
        style,
        file,
        finish = true,
        progress = 0,
        hasDeleteBtn = true,
        hasDownloadBtn,
        uploadStyle,
        withLocalPreview = false,
        fallbackPreview,
        maxWidth = 65,
        maxHeight = 65,
        onDelete = EMPTY_FN,
        onClick = EMPTY_FN,
        onDownload = EMPTY_FN
    } = props;
    const thumbUrl = (file as IFile).thumbUrl || getPublicImageUrl(file);

    const previewRef = useRef<any>();

    const region = useContext(RegionContext);

    useEffect(() => {
        if ((withLocalPreview || (fallbackPreview && !thumbUrl)) && previewRef.current) {
            const url = window.URL || window.webkitURL;
            const src = url.createObjectURL(file as File);
            const img = new Image();
            img.src = src;
            const ctx = previewRef.current.getContext('2d');
            ctx.canvas.width = maxWidth * 2;
            ctx.canvas.height = maxHeight * 2;
            img.onload = () => {
                const wRatio = img.width / maxWidth;
                const hRatio = img.height / maxHeight;
                if (wRatio > hRatio) {
                    ctx.drawImage(
                        img,
                        (img.width - maxWidth * hRatio) / 2,
                        0,
                        maxWidth * hRatio,
                        img.height,
                        0,
                        0,
                        maxWidth * 2,
                        maxHeight * 2
                    );
                } else {
                    ctx.drawImage(
                        img,
                        0,
                        (img.height - maxHeight * wRatio) / 2,
                        img.width,
                        maxHeight * wRatio,
                        0,
                        0,
                        maxWidth * 2,
                        maxHeight * 2
                    );
                }
                url.revokeObjectURL(src);
            };
        }
    }, [file]);

    const [fileName, extName] = parseFileName(get(file, 'name'));

    const renderUploadPreview = () => {
        if (isImageExt(extName) && file.size < MAX_IMAGE_VIEW_SIZE) {
            if (withLocalPreview || (fallbackPreview && !thumbUrl)) {
                return <canvas className="preview-canvas" ref={previewRef} />;
            } else {
                return <img src={thumbUrl} />;
            }
        } else {
            return <FileThumb className="fx-upload-file-thumb" ext={extName} />;
        }
    };

    const renderProgress = () => {
        return !finish && <Progress className="upload-progress" value={progress} />;
    };

    const renderUploadFilePopoverInfo = () => {
        return (
            <>
                <div>{`${fileName}.${extName}`}</div>
                <div className="popover-footer">
                    <div className="info-size">{formatFileSize(file.size, region)}</div>
                    {hasDownloadBtn && (
                        <div
                            className="btn-wrapper"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDownload();
                            }}
                        >
                            <IconFont className="btn-download" type="download" />
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderBasicFileInfo = () => {
        return (
            <div className="upload-file-info">
                <div className="info-text">
                    <span className="text-info">{fileName}</span>
                    {!isEmpty(extName) && <span className="text-ext">{`.${extName}`}</span>}
                </div>
                <div className="info-size">{formatFileSize(file.size, region)}</div>
            </div>
        );
    };

    return (
        <div
            className={classNames('fx-file-item', className, {
                'has-delete': hasDeleteBtn,
                'has-download': hasDownloadBtn
            })}
            style={style}
            onClick={onClick}
        >
            <div className="upload-preview">{renderUploadPreview()}</div>
            {uploadStyle === UPLOAD_STYLE.SHORT ? (
                <Popover
                    className="upload-file-popover"
                    content={renderUploadFilePopoverInfo()}
                    placement="top"
                >
                    {renderBasicFileInfo()}
                </Popover>
            ) : (
                renderBasicFileInfo()
            )}
            {hasDeleteBtn && (
                <div
                    className="upload-info-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                >
                    <IconFont className="upload-btn-del" type="close-circle" />
                </div>
            )}
            {uploadStyle !== UPLOAD_STYLE.SHORT && hasDownloadBtn && (
                <div
                    className="upload-info-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDownload();
                    }}
                >
                    <IconFont className="upload-btn-download" type="download" />
                </div>
            )}
            {renderProgress()}
        </div>
    );
};

export default FileItem;
